/**
 * buildShellProps — map a serializable per-app chrome descriptor to the
 * `MarketingShell` header/footer props (BLOG-STD-001, full-shell SSR).
 *
 * The chrome descriptor is the JSON-able, per-locale shape produced by each
 * app's `buildMarketingChromeDescriptor({ t })`. It carries everything
 * `MarketingHeader` + `MarketingFooter` render EXCEPT the two non-serializable
 * bits, which this mapper re-injects at call time:
 *   - `header.localeSwitcher` → `headerProps.localeProps`, adding the site's
 *     StorageService instance as `storage` (browser) or omitting it (server harness — `LocaleSwitcher`
 *     treats it as optional), plus `initialLocale` so the switcher's first render
 *     matches the URL locale.
 *   - `footer.extraColumns` (kind-tagged data) → React nodes via the shared
 *     `renderFooterExtraColumns`.
 *
 * **Both the client app AND the server prerender harness call this with the same
 * descriptor + locale**, so they produce identical `MarketingShell` props → the
 * server-rendered HTML hydrates with zero mismatch. Single source of truth for
 * descriptor → props (factory-first).
 *
 * @module @puffin-core/marketing-shell/buildShellProps
 */

import { renderFooterExtraColumns } from '../components/renderFooterExtraColumn.jsx';
import { readSsrSeedFromDom } from '../blog/readBlogSsrSeed.js';

/**
 * @typedef {Object} ChromeDescriptor
 * @property {string} appKey
 * @property {{appName:string,wordmarkPrefix:string,wordmarkSuffix:string,tagline?:string,glyphSrc?:string}} [brandIdentity]
 *   Self-describing brand chrome for white-label vanity appKeys (BLOG-VANITY-SSR).
 *   When present, MarketingHeader/MarketingFooter use it instead of `getBrand(appKey)`.
 * @property {Object} header
 * @property {string} [header.navLabel] - names the header's navigation landmark
 * @property {Array} header.navLinks
 * @property {boolean} [header.showEcosystemSwitcher]
 * @property {{label:string,href:string}} [header.signInCta]
 * @property {{label:string,href:string}} [header.secondaryCta]
 * @property {string[]} [header.hideCtasOnPaths]
 * @property {{languages:Array<{code:string,label:string}>,storageKey:string,storageTypeLocal:string}} [header.localeSwitcher]
 * @property {Object} footer
 * @property {string} [footer.tagline]
 * @property {Array} [footer.columns]
 * @property {Array} [footer.socialLinks]
 * @property {string} [footer.privacyLabel]
 * @property {string} [footer.privacyTo]
 * @property {string} [footer.termsLabel]
 * @property {string} [footer.termsTo]
 * @property {string} [footer.phone]
 * @property {string} [footer.phoneDisplay]
 * @property {string} [footer.phoneColumnHeading]
 * @property {string} [footer.copyrightOwner] - the tenant site's legal entity (copyright line)
 * @property {string} [footer.disclosure] - the tenant site's site-wide disclosure
 * @property {?string} [footer.disclosurePageTo]
 * @property {string} [footer.disclosurePageLabel]
 * @property {?string} [footer.privacyRequestTo] - the tenant site's privacy-request page
 * @property {string} [footer.privacyRequestLabel]
 * @property {?{prefix:string,label:string,href:string,rel:string}} [footer.attribution] - the tenant
 *   site's platform credit (LINKABLE-ASSETS-001 E1), null when hidden
 * @property {string} [footer.ariaLabel] - the footer landmark's label
 * @property {Array<{kind:string}>} [footer.extraColumns]
 */

/**
 * @param {Object} args
 * @param {ChromeDescriptor} args.descriptor
 * @param {string} args.locale                 Render locale (e.g. 'en' | 'es'); seeds the
 *                                             LocaleSwitcher's `initialLocale`.
 * @param {string} [args.defaultLocale]        The locale that owns the unprefixed blog URLs;
 *                                             seeds the LocaleSwitcher's `defaultLocale`, so its
 *                                             blog rewrite agrees with the factory's route variants.
 * @param {number} [args.copyrightYear]        Seeded footer year (prerender determinism).
 * @param {Object} [args.storage]             The site's ONE StorageService instance (browser). Omit on
 *                                             the server harness — the switcher persists nothing there.
 * @param {(href: string, destinationKind: string) => void} [args.onOutboundClick]  The client's
 *   outbound-click reporter, threaded to the header's links and the footer's link columns. Omit on
 *   the server.
 * @returns {{appKey:string, headerProps:Object, footerProps:Object}}
 */
export default function buildShellProps({
  descriptor,
  locale,
  defaultLocale,
  copyrightYear,
  storage,
  onOutboundClick,
}) {
  if (!descriptor || typeof descriptor !== 'object') {
    throw new TypeError('buildShellProps: descriptor object is required');
  }
  if (!descriptor.appKey || typeof descriptor.appKey !== 'string') {
    throw new TypeError('buildShellProps: descriptor.appKey (string) is required');
  }
  const header = descriptor.header || {};
  const footer = descriptor.footer || {};
  // The descriptor carries the SITE's words, pre-resolved for the render locale. The shell's OWN
  // words (skip link, menu button, switchers, copyright) are marketing-shell's namespace, which the
  // client's i18n and the blog's server i18n (`createSeededI18n`) both carry — so they match on
  // hydration without riding the descriptor (Q-LC24-7's #418; LIFE-SHORTLISTED-001 F-Q7-1).

  // BLOG-STD-001 full-shell SSR: the server passes `copyrightYear` explicitly so
  // the footer year is baked into the prerendered HTML. On the client (where the
  // app calls buildShellProps without a year), read the seeded value from the
  // `<script type="application/json" id="__blog-ssr-seed__">` data block so the
  // hydrated footer matches the server markup across a New-Year boundary. Absent
  // on both → MarketingFooter falls back to the current year (unchanged behavior
  // for non-SSR marketing pages). Uses the canonical `readSsrSeedFromDom` reader
  // so the lookup + parse is single-source-of-truth with `readBlogSsrSeed`.
  let resolvedCopyrightYear;
  if (Number.isFinite(copyrightYear)) {
    resolvedCopyrightYear = copyrightYear;
  } else {
    const ssr = readSsrSeedFromDom();
    resolvedCopyrightYear =
      ssr && Number.isFinite(ssr.copyrightYear) ? ssr.copyrightYear : undefined;
  }

  const ls = header.localeSwitcher;
  const localeProps = ls
    ? {
        languages: ls.languages,
        storageKey: ls.storageKey,
        STORAGE_TYPE_LOCAL: ls.storageTypeLocal,
        storage,
        initialLocale: locale,
        defaultLocale,
      }
    : undefined;

  // BLOG-VANITY-SSR: white-label vanity brands (customcarvers / goldmark /
  // protector blog appKeys) are served by purchase-web, whose deploy ships ONLY
  // PurchasePuffin's `/branding/` assets and whose `@puffin-core/brand-assets`
  // registry may not carry the blog-only appKey at all (customcarvers is absent).
  // A descriptor MAY therefore carry a self-describing `brandIdentity`
  // ({ appName, wordmarkPrefix, wordmarkSuffix, tagline?, glyphSrc? }). When
  // present, MarketingHeader/MarketingFooter render it INSTEAD of calling
  // `getBrand(appKey)` — so the shell never (a) throws for an appKey missing from
  // brand-assets, nor (b) renders PurchasePuffin's glyph PNG on a vanity domain.
  // Absent (the 9 ParcelPuffin-family apps) → the components fall back to `getBrand`
  // unchanged, so this is fully backward-compatible.
  const brandIdentity =
    descriptor.brandIdentity && typeof descriptor.brandIdentity === 'object'
      ? descriptor.brandIdentity
      : undefined;

  const headerProps = {
    navLinks: header.navLinks,
    // LIFE-SHORTLISTED-001 Q-LS-32 — the tenant chrome names its nav landmark.
    navLabel: header.navLabel,
    onOutboundClick,
    showEcosystemSwitcher: header.showEcosystemSwitcher,
    signInCta: header.signInCta,
    secondaryCta: header.secondaryCta,
    hideCtasOnPaths: header.hideCtasOnPaths,
    brandIdentity,
    localeProps,
  };

  const footerProps = {
    brandIdentity,
    tagline: footer.tagline,
    columns: footer.columns,
    socialLinks: footer.socialLinks,
    privacyLabel: footer.privacyLabel,
    privacyTo: footer.privacyTo,
    termsLabel: footer.termsLabel,
    termsTo: footer.termsTo,
    phone: footer.phone,
    phoneDisplay: footer.phoneDisplay,
    phoneColumnHeading: footer.phoneColumnHeading,
    // LIFE-SHORTLISTED-001 Q-LS-32 — the tenant site's footer, which a tenant blog's chrome is:
    // its legal entity names the copyright line, its disclosure shows in every footer, and a legal
    // page it did not declare is null (no link).
    copyrightOwner: footer.copyrightOwner,
    disclosure: footer.disclosure,
    disclosurePageTo: footer.disclosurePageTo,
    disclosurePageLabel: footer.disclosurePageLabel,
    privacyRequestTo: footer.privacyRequestTo,
    privacyRequestLabel: footer.privacyRequestLabel,
    // LINKABLE-ASSETS-001 E1 — the platform credit, built once by `buildTenantFooter` and carried as
    // data, so the server-rendered blog footer and the live site footer show the same link.
    attribution: footer.attribution,
    ariaLabel: footer.ariaLabel,
    copyrightYear: resolvedCopyrightYear,
    extraColumns: Array.isArray(footer.extraColumns)
      ? renderFooterExtraColumns(footer.extraColumns, { onOutboundClick })
      : undefined,
  };

  return {
    appKey: descriptor.appKey,
    headerProps,
    footerProps,
  };
}
